import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Filter,
  Download,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  CreditCard,
  FileText,
  Ban
} from 'lucide-react';
import { receitaService, formatCurrency, formatDate } from '@/services';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const Receitas = () => {
  const { toast } = useToast();

  // Estados principais
  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [stats, setStats] = useState(null);

  // Estados de modais
  const [modalOpen, setModalOpen] = useState(false);
  const [pagamentoModalOpen, setPagamentoModalOpen] = useState(false);
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receitaSelecionada, setReceitaSelecionada] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    cliente_id: '',
    cliente_nome: '',
    contrato_id: '',
    tipo: 'mensalidade',
    descricao: '',
    valor_original: '',
    valor_desconto: '0',
    data_emissao: new Date().toISOString().split('T')[0],
    data_vencimento: '',
    metodo_pagamento: 'boleto',
    observacoes: ''
  });

  // Estados do formulário de pagamento
  const [pagamentoData, setPagamentoData] = useState({
    data_pagamento: new Date().toISOString().split('T')[0],
    valor_pago: '',
    metodo_pagamento: 'boleto',
    comprovante_url: '',
    observacoes: ''
  });

  useEffect(() => {
    loadReceitas();
    loadStats();
  }, []);

  const loadReceitas = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus !== 'todos') params.status = filtroStatus;
      if (filtroTipo !== 'todos') params.tipo = filtroTipo;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;

      const data = await receitaService.getAll(params);
      setReceitas(data.receitas || []);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await receitaService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!formData.cliente_nome || !formData.descricao || !formData.valor_original || !formData.data_vencimento) {
        toast({
          title: 'Erro',
          description: 'Preencha todos os campos obrigatórios',
          variant: 'destructive',
        });
        return;
      }

      const dados = {
        ...formData,
        valor_original: parseFloat(formData.valor_original),
        valor_desconto: parseFloat(formData.valor_desconto || 0),
      };

      if (modoEdicao && receitaSelecionada) {
        const receitaId = receitaSelecionada.id || receitaSelecionada._id;
        if (!receitaId) {
          toast({
            title: 'Erro',
            description: 'ID da receita não encontrado',
            variant: 'destructive',
          });
          return;
        }

        await receitaService.update(receitaId, dados);
        toast({
          title: 'Sucesso',
          description: 'Receita atualizada com sucesso',
        });
      } else if (!modoEdicao) {
        await receitaService.create(dados);
        toast({
          title: 'Sucesso',
          description: 'Receita criada com sucesso',
        });
      }

      setModalOpen(false);
      resetForm();
      loadReceitas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleRegistrarPagamento = async (e) => {
    e.preventDefault();
    
    try {
      if (!receitaSelecionada) {
        toast({
          title: 'Erro',
          description: 'Nenhuma receita selecionada',
          variant: 'destructive',
        });
        return;
      }

      const receitaId = receitaSelecionada.id || receitaSelecionada._id;
      if (!receitaId) {
        toast({
          title: 'Erro',
          description: 'ID da receita não encontrado',
          variant: 'destructive',
        });
        return;
      }

      if (!pagamentoData.valor_pago) {
        toast({
          title: 'Erro',
          description: 'Informe o valor pago',
          variant: 'destructive',
        });
        return;
      }

      const dados = {
        ...pagamentoData,
        valor_pago: parseFloat(pagamentoData.valor_pago),
      };

      await receitaService.registrarPagamento(receitaId, dados);
      
      toast({
        title: 'Sucesso',
        description: 'Pagamento registrado com sucesso',
      });

      setPagamentoModalOpen(false);
      setReceitaSelecionada(null);
      resetPagamentoForm();
      loadReceitas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleAplicarJuros = async (receita) => {
    try {
      if (!receita) {
        toast({
          title: 'Erro',
          description: 'Receita inválida',
          variant: 'destructive',
        });
        return;
      }

      const receitaId = receita.id || receita._id;
      if (!receitaId) {
        toast({
          title: 'Erro',
          description: 'ID da receita não encontrado',
          variant: 'destructive',
        });
        return;
      }

      await receitaService.aplicarJuros(receitaId);
      toast({
        title: 'Sucesso',
        description: 'Juros e multa aplicados com sucesso',
      });
      loadReceitas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (receita) => {
    setReceitaSelecionada(receita);
    setModoEdicao(true);
    setFormData({
      cliente_id: receita.cliente_id || '',
      cliente_nome: receita.cliente_nome || '',
      contrato_id: receita.contrato_id || '',
      tipo: receita.tipo || 'mensalidade',
      descricao: receita.descricao || '',
      valor_original: receita.valor_original?.toString() || '',
      valor_desconto: receita.valor_desconto?.toString() || '0',
      data_emissao: receita.data_emissao?.split('T')[0] || '',
      data_vencimento: receita.data_vencimento?.split('T')[0] || '',
      metodo_pagamento: receita.metodo_pagamento || 'boleto',
      observacoes: receita.observacoes || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (!receitaSelecionada) {
        toast({
          title: 'Erro',
          description: 'Nenhuma receita selecionada',
          variant: 'destructive',
        });
        return;
      }

      const receitaId = receitaSelecionada.id || receitaSelecionada._id;
      if (!receitaId) {
        toast({
          title: 'Erro',
          description: 'ID da receita não encontrado',
          variant: 'destructive',
        });
        return;
      }

      await receitaService.delete(receitaId);
      toast({
        title: 'Sucesso',
        description: 'Receita cancelada com sucesso',
      });
      setDeleteDialogOpen(false);
      setReceitaSelecionada(null);
      loadReceitas();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleVerDetalhes = async (receita) => {
    try {
      const receitaId = receita.id || receita._id;
      if (!receitaId) {
        toast({
          title: 'Erro',
          description: 'ID da receita não encontrado',
          variant: 'destructive',
        });
        return;
      }

      const detalhes = await receitaService.getById(receitaId);
      setReceitaSelecionada(detalhes);
      setDetalhesModalOpen(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handlePagarReceita = (receita) => {
    setReceitaSelecionada(receita);
    setPagamentoData({
      ...pagamentoData,
      valor_pago: receita.valor_final?.toString() || receita.valor_original?.toString() || ''
    });
    setPagamentoModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      cliente_nome: '',
      contrato_id: '',
      tipo: 'mensalidade',
      descricao: '',
      valor_original: '',
      valor_desconto: '0',
      data_emissao: new Date().toISOString().split('T')[0],
      data_vencimento: '',
      metodo_pagamento: 'boleto',
      observacoes: ''
    });
    setModoEdicao(false);
    setReceitaSelecionada(null);
  };

  const resetPagamentoForm = () => {
    setPagamentoData({
      data_pagamento: new Date().toISOString().split('T')[0],
      valor_pago: '',
      metodo_pagamento: 'boleto',
      comprovante_url: '',
      observacoes: ''
    });
  };

  const getStatusBadge = (receita) => {
    const status = receita.status;
    const configs = {
      pago: { label: 'Pago', className: 'bg-green-100 text-green-800 hover:bg-green-100', icon: CheckCircle },
      em_aberto: { label: 'Em Aberto', className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', icon: Clock },
      atrasado: { label: 'Atrasado', className: 'bg-red-100 text-red-800 hover:bg-red-100', icon: AlertCircle },
      parcial: { label: 'Pago Parcial', className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100', icon: Clock },
      cancelado: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', icon: Ban }
    };

    const config = configs[status] || configs.em_aberto;
    const Icon = config.icon;

    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const calcularDiasAtraso = (dataVencimento) => {
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    const diffTime = hoje - vencimento;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Filtrar receitas
  const receitasFiltradas = receitas.filter(receita => {
    const matchSearch = receita.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       receita.numero_fatura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       receita.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchSearch;
  });

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-500">Carregando receitas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Receitas / Faturas</h1>
          <p className="text-gray-500 mt-1">Gerencie as receitas e faturas da empresa</p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Receita
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total a Receber</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(stats.total_a_receber)}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats.faturas_abertas} faturas em aberto</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Recebido</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(stats.total_recebido)}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats.faturas_pagas} faturas pagas</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Atrasadas</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(stats.total_atrasado)}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats.faturas_atrasadas} faturas atrasadas</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taxa de Conversão</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.taxa_conversao?.toFixed(1)}%
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.total_faturas_mes} faturas no mês
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por cliente, número ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={filtroStatus} onValueChange={(value) => { setFiltroStatus(value); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Status</SelectItem>
                <SelectItem value="em_aberto">Em Aberto</SelectItem>
                <SelectItem value="atrasado">Atrasadas</SelectItem>
                <SelectItem value="pago">Pagas</SelectItem>
                <SelectItem value="parcial">Pago Parcial</SelectItem>
                <SelectItem value="cancelado">Canceladas</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filtroTipo} onValueChange={(value) => { setFiltroTipo(value); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Tipos</SelectItem>
                <SelectItem value="mensalidade">Mensalidade</SelectItem>
                <SelectItem value="projeto">Projeto</SelectItem>
                <SelectItem value="servico_avulso">Serviço Avulso</SelectItem>
                <SelectItem value="setup">Setup</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={loadReceitas}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {/* Filtro de Data */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label htmlFor="data_inicio" className="text-xs text-gray-600">Data Início</Label>
              <Input
                id="data_inicio"
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="data_fim" className="text-xs text-gray-600">Data Fim</Label>
              <Input
                id="data_fim"
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Receitas */}
      {receitasFiltradas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">Nenhuma receita encontrada</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm || filtroStatus !== 'todos' || filtroTipo !== 'todos'
                ? 'Tente ajustar os filtros'
                : 'Crie sua primeira receita clicando no botão acima'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {receitasFiltradas.map((receita) => {
            const diasAtraso = calcularDiasAtraso(receita.data_vencimento);

            return (
              <Card key={receita.id} className={`hover:shadow-lg transition-shadow ${
                receita.status === 'atrasado' ? 'border-red-200 bg-red-50' : ''
              }`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    {/* Informações Principais */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {receita.numero_fatura}
                        </h3>
                        {getStatusBadge(receita)}
                        {receita.status === 'atrasado' && diasAtraso > 0 && (
                          <Badge variant="destructive">
                            {diasAtraso} {diasAtraso === 1 ? 'dia' : 'dias'} de atraso
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">Cliente</p>
                          <p className="font-medium text-gray-900">{receita.cliente_nome}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Descrição</p>
                          <p className="font-medium text-gray-900">{receita.descricao}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Vencimento</p>
                          <p className="font-medium text-gray-900 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(receita.data_vencimento)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Valor</p>
                          <p className="text-xl font-bold text-gray-900">
                            {formatCurrency(receita.valor_final || receita.valor_original)}
                          </p>
                        </div>
                      </div>

                      {/* Informações Adicionais para Atrasadas */}
                      {receita.status === 'atrasado' && receita.juros_calculado && (
                        <div className="mt-4 p-3 bg-red-100 rounded-lg">
                          <p className="text-sm font-medium text-red-900">Juros e Multa Calculados:</p>
                          <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                            <div>
                              <span className="text-red-700">Multa:</span>
                              <span className="ml-2 font-semibold">{formatCurrency(receita.juros_calculado.multa)}</span>
                            </div>
                            <div>
                              <span className="text-red-700">Juros:</span>
                              <span className="ml-2 font-semibold">{formatCurrency(receita.juros_calculado.juros)}</span>
                            </div>
                            <div>
                              <span className="text-red-700">Total:</span>
                              <span className="ml-2 font-semibold">{formatCurrency(receita.juros_calculado.valor_final)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Data de Pagamento para Pagas */}
                      {receita.status === 'pago' && receita.data_pagamento && (
                        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span>Pago em: {formatDate(receita.data_pagamento)}</span>
                          </div>
                          <div>
                            <span>Valor pago: </span>
                            <span className="font-semibold">{formatCurrency(receita.valor_pago)}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ações */}
                    <div className="flex flex-col gap-2">
                      {receita.status !== 'pago' && receita.status !== 'cancelado' && (
                        <Button
                          size="sm"
                          onClick={() => handlePagarReceita(receita)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Registrar Pagamento
                        </Button>
                      )}

                      {receita.status === 'atrasado' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAplicarJuros(receita)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          Aplicar Juros
                        </Button>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleVerDetalhes(receita)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Detalhes
                          </DropdownMenuItem>
                          {receita.status !== 'pago' && (
                            <>
                              <DropdownMenuItem onClick={() => handleEdit(receita)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setReceitaSelecionada(receita);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Cancelar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de Criar/Editar Receita */}
      <Dialog open={modalOpen} onOpenChange={(open) => {
        setModalOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modoEdicao ? 'Editar Receita' : 'Nova Receita'}
            </DialogTitle>
            <DialogDescription>
              {modoEdicao ? 'Atualize as informações da receita' : 'Preencha os dados da nova receita'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Cliente */}
            <div className="space-y-2">
              <Label htmlFor="cliente_nome">Cliente *</Label>
              <Input
                id="cliente_nome"
                value={formData.cliente_nome}
                onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                placeholder="Nome do cliente"
                required
              />
            </div>

            {/* Tipo e Descrição */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensalidade">Mensalidade</SelectItem>
                    <SelectItem value="projeto">Projeto</SelectItem>
                    <SelectItem value="servico_avulso">Serviço Avulso</SelectItem>
                    <SelectItem value="setup">Setup</SelectItem>
                    <SelectItem value="multa">Multa</SelectItem>
                    <SelectItem value="juros">Juros</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="metodo_pagamento">Método de Pagamento</Label>
                <Select
                  value={formData.metodo_pagamento}
                  onValueChange={(value) => setFormData({ ...formData, metodo_pagamento: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Mensalidade Dezembro/2024"
                required
              />
            </div>

            {/* Valores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor_original">Valor Original *</Label>
                <Input
                  id="valor_original"
                  type="number"
                  step="0.01"
                  value={formData.valor_original}
                  onChange={(e) => setFormData({ ...formData, valor_original: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor_desconto">Desconto</Label>
                <Input
                  id="valor_desconto"
                  type="number"
                  step="0.01"
                  value={formData.valor_desconto}
                  onChange={(e) => setFormData({ ...formData, valor_desconto: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_emissao">Data de Emissão *</Label>
                <Input
                  id="data_emissao"
                  type="date"
                  value={formData.data_emissao}
                  onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                <Input
                  id="data_vencimento"
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Notas internas sobre esta receita"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {modoEdicao ? 'Atualizar' : 'Criar'} Receita
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Registrar Pagamento */}
      <Dialog open={pagamentoModalOpen} onOpenChange={setPagamentoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
            <DialogDescription>
              {receitaSelecionada?.numero_fatura} - {receitaSelecionada?.cliente_nome}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRegistrarPagamento} className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Valor da Fatura</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(receitaSelecionada?.valor_final || receitaSelecionada?.valor_original)}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="data_pagamento">Data do Pagamento *</Label>
              <Input
                id="data_pagamento"
                type="date"
                value={pagamentoData.data_pagamento}
                onChange={(e) => setPagamentoData({ ...pagamentoData, data_pagamento: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor_pago">Valor Pago *</Label>
              <Input
                id="valor_pago"
                type="number"
                step="0.01"
                value={pagamentoData.valor_pago}
                onChange={(e) => setPagamentoData({ ...pagamentoData, valor_pago: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="metodo_pagamento_pag">Método de Pagamento *</Label>
              <Select
                value={pagamentoData.metodo_pagamento}
                onValueChange={(value) => setPagamentoData({ ...pagamentoData, metodo_pagamento: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comprovante_url">Link do Comprovante (opcional)</Label>
              <Input
                id="comprovante_url"
                value={pagamentoData.comprovante_url}
                onChange={(e) => setPagamentoData({ ...pagamentoData, comprovante_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes_pag">Observações</Label>
              <Textarea
                id="observacoes_pag"
                value={pagamentoData.observacoes}
                onChange={(e) => setPagamentoData({ ...pagamentoData, observacoes: e.target.value })}
                placeholder="Notas sobre o pagamento"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPagamentoModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirmar Pagamento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes */}
      <Dialog open={detalhesModalOpen} onOpenChange={setDetalhesModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{receitaSelecionada?.numero_fatura}</span>
              {receitaSelecionada && getStatusBadge(receitaSelecionada)}
            </DialogTitle>
            <DialogDescription>{receitaSelecionada?.cliente_nome}</DialogDescription>
          </DialogHeader>

          {receitaSelecionada && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Tipo</Label>
                  <p className="font-medium capitalize">{receitaSelecionada.tipo?.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Método de Pagamento</Label>
                  <p className="font-medium capitalize">{receitaSelecionada.metodo_pagamento?.replace('_', ' ')}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-500">Valor Original</Label>
                  <p className="text-lg font-bold">{formatCurrency(receitaSelecionada.valor_original)}</p>
                </div>
                {receitaSelecionada.valor_desconto > 0 && (
                  <div>
                    <Label className="text-gray-500">Desconto</Label>
                    <p className="text-lg font-bold text-green-600">
                      -{formatCurrency(receitaSelecionada.valor_desconto)}
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-gray-500">Valor Final</Label>
                  <p className="text-lg font-bold text-purple-600">
                    {formatCurrency(receitaSelecionada.valor_final || receitaSelecionada.valor_original)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Data de Emissão</Label>
                  <p className="font-medium">{formatDate(receitaSelecionada.data_emissao)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Data de Vencimento</Label>
                  <p className="font-medium">{formatDate(receitaSelecionada.data_vencimento)}</p>
                </div>
              </div>

              {receitaSelecionada.data_pagamento && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <Label className="text-green-900">Pagamento Realizado</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-green-700">Data</p>
                      <p className="font-semibold">{formatDate(receitaSelecionada.data_pagamento)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700">Valor Pago</p>
                      <p className="font-semibold">{formatCurrency(receitaSelecionada.valor_pago)}</p>
                    </div>
                  </div>
                </div>
              )}

              {receitaSelecionada.observacoes && (
                <div>
                  <Label className="text-gray-500">Observações</Label>
                  <p className="text-gray-700 mt-1">{receitaSelecionada.observacoes}</p>
                </div>
              )}

              {receitaSelecionada.cliente_info && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-gray-500">Informações do Cliente</Label>
                  <div className="mt-2 space-y-1 text-sm">
                    {receitaSelecionada.cliente_info.email && (
                      <p><span className="text-gray-600">Email:</span> {receitaSelecionada.cliente_info.email}</p>
                    )}
                    {receitaSelecionada.cliente_info.telefone && (
                      <p><span className="text-gray-600">Telefone:</span> {receitaSelecionada.cliente_info.telefone}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalhesModalOpen(false)}>
              Fechar
            </Button>
            {receitaSelecionada?.status !== 'pago' && receitaSelecionada?.status !== 'cancelado' && (
              <>
                <Button onClick={() => { setDetalhesModalOpen(false); handleEdit(receitaSelecionada); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => { setDetalhesModalOpen(false); handlePagarReceita(receitaSelecionada); }}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Registrar Pagamento
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação cancelará a receita <strong>{receitaSelecionada?.numero_fatura}</strong>.
              A receita será marcada como cancelada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sim, Cancelar Receita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Receitas;
